import { PublicApiClient } from '@tf/api';
import { ActionIcon, Icon, Menu, TFNotifier, Tooltip } from '@tf/ui';
import { saveFile } from '@tf/utils';
import type { FC } from 'react';
import { useState } from 'react';

import { useSelectedAccount } from '@/core/hooks';
import { UserCapabilities } from '@/core/types';
import { useCurrentUserCapabilityCheck } from '@/core/utils';

type ExportType = 'pdf' | 'archive';

async function downloadProfileFile(profileId: string, type: ExportType, profileName: string): Promise<void> {
	const endpoint = type === 'pdf' ? 'profile-pdf' : 'profile-export';
	const extension = type === 'pdf' ? 'pdf' : 'zip';
	const fileName = `${profileName || 'profile'}.${extension}`;

	const api = PublicApiClient.getInstance();
	const response = await api.request({
		path: `/v1/data/${endpoint}?profileId=${encodeURIComponent(profileId)}`,
		method: 'GET',
	});

	if (!response.ok) {
		throw new Error(`Failed to download: HTTP ${response.status}`);
	}

	const buf = await response.arrayBuffer();
	saveFile(buf, fileName);
}

export const ExportProfileButton: FC = () => {
	const canExportAccountData = useCurrentUserCapabilityCheck(UserCapabilities.CAN_EXPORT_ACCOUNT_DATA);
	const { meta, profileId } = useSelectedAccount();
	const [loading, setLoading] = useState<ExportType | null>(null);

	const handleDownload = async (type: ExportType) => {
		setLoading(type);
		try {
			await downloadProfileFile(profileId, type, meta.name);
			TFNotifier.info('Download started');
		} catch (err) {
			console.error('Error exporting profile:', err);
			TFNotifier.error('Failed to export profile');
		} finally {
			setLoading(null);
		}
	};

	const isLoading = loading !== null;

	if (!canExportAccountData) {
		return null;
	}

	return (
		<Menu position="bottom-end" disabled={isLoading}>
			<Menu.Target>
				<Tooltip label="Export profile" withArrow>
					<ActionIcon variant="light" size="lg" aria-label="Export profile" loading={isLoading}>
						<Icon.IconFileDownload size={18} />
					</ActionIcon>
				</Tooltip>
			</Menu.Target>
			<Menu.Dropdown>
				<Menu.Item
					leftSection={<Icon.IconFileTypePdf size={16} />}
					onClick={() => handleDownload('pdf')}
					disabled={isLoading}
				>
					Download as PDF
				</Menu.Item>
				<Menu.Item
					leftSection={<Icon.IconFileZip size={16} />}
					onClick={() => handleDownload('archive')}
					disabled={isLoading}
				>
					Download as Archive (ZIP)
				</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	);
};
